/**
 * CharterGroupSidebar — Phase-2/3 "Operations" sidebar content.
 *
 * Ratified-charter card + session stage progress + turn participation +
 * team profiles. Reads charter group WS state and REST profile state from
 * Redux; owns only its own modal UI state.
 */
import { useMemo, useState } from 'react'
import { useAppSelector } from '@/redux/hooks'
import {
  selectCharterSession,
  selectRatifiedCharter,
  selectCurrentStage,
  selectTurnTracker,
  selectRenegotiationMode,
  selectCharterVersion,
  selectCharterStatus,
  selectMyPrepProfile,
  selectTeamProfiles,
} from '../../redux/selectors/charterSelectors'
import { StageProgressBar } from '../Phase2/StageProgressBar'
import { TurnIndicator } from '../Phase2/TurnIndicator'
import { TeamProfilesPanel } from '../Phase2/TeamProfilesPanel'
import { RatifiedCharterCard } from '../Charter/RatifiedCharterCard'
import { CharterCardModal } from '../Charter/CharterCardModal'

interface CharterGroupSidebarProps {
  roomId: string
  monitor?: boolean
}

export function CharterGroupSidebar({
  roomId,
  monitor = false,
}: CharterGroupSidebarProps) {
  const session = useAppSelector(selectCharterSession)
  const ratifiedCharter = useAppSelector(selectRatifiedCharter)
  const currentStage = useAppSelector(selectCurrentStage)
  const turnTracker = useAppSelector(selectTurnTracker)
  const renegotiationMode = useAppSelector(selectRenegotiationMode)
  const charterStatus = useAppSelector(selectCharterStatus)
  const charterVersion = useAppSelector(selectCharterVersion)
  const myProfile = useAppSelector(selectMyPrepProfile)
  const teamProfiles = useAppSelector(selectTeamProfiles)

  const currentUser = useAppSelector((s) => s.user.user)
  const currentUserId = String(currentUser?.id ?? '')

  const [isCharterModalOpen, setIsCharterModalOpen] = useState(false)

  // Real display names keyed by RoomMember id, so participation reads
  // "Alex / Jordan" rather than "Member 41 / Member 42".
  const memberNames = useMemo(
    () =>
      Object.fromEntries(
        [myProfile, ...teamProfiles]
          .filter((p): p is NonNullable<typeof p> => Boolean(p))
          .map((p) => [String(p.member_id), p.member_name])
      ) as Record<string, string>,
    [myProfile, teamProfiles]
  )

  return (
    <div className='px-2 space-y-5'>
      {/* Ratified charter card */}
      {(ratifiedCharter || session?.ratified_charter) && (
        <div>
          <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1'>
            Team Charter
          </p>
          <RatifiedCharterCard
            onClick={() => setIsCharterModalOpen(true)}
            charterVersion={charterVersion ?? session?.version ?? 1}
          />
        </div>
      )}

      {/* Stage progress */}
      <div>
        <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1'>
          Session Progress
        </p>
        <StageProgressBar
          currentStage={currentStage}
          amendmentMode={renegotiationMode}
          allComplete={
            !renegotiationMode &&
            (charterStatus === 'locked' || session?.status === 'locked')
          }
        />
      </div>

      {/* Turn participation */}
      {Object.keys(turnTracker).length > 0 && (
        <div>
          <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1'>
            Participation
          </p>
          <TurnIndicator
            turnTracker={turnTracker}
            memberNames={memberNames}
            currentUserId={currentUserId}
          />
        </div>
      )}

      {/* Team profiles */}
      {(myProfile || teamProfiles.length > 0) && (
        <div>
          <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1'>
            User Profile
          </p>
          <TeamProfilesPanel myProfile={myProfile} teamProfiles={teamProfiles} />
        </div>
      )}

      <CharterCardModal
        isOpen={isCharterModalOpen}
        onClose={() => setIsCharterModalOpen(false)}
        roomId={roomId}
        isStudent={!monitor}
        teamId={session?.team_id}
      />
    </div>
  )
}
